import React, {
  ReactElement,
  memo,
  useState,
  useCallback,
  useMemo,
  useRef,
  ReactNode,
  forwardRef,
  ForwardedRef,
  useEffect,
  MutableRefObject,
  KeyboardEventHandler,
} from 'react';

import _noop from 'lodash/noop';

import Input, { Formatter, Parser } from '@ott/input';
import { useLayoutContext } from '@ott/layout-context';
import Select, {
  SelectProps,
  SelectCustomRenderer,
  CustomTriggerProps,
  SelectOption,
  getParsedSelectOptions,
  OptionGroupProps,
  isOptionGroup,
  CustomOptionProps,
  AlignType,
  ALIGN_TYPES,
} from '@ott/select';

import { KEYS } from './helpers/constants';
import defaultOptionFilter from './helpers/defaultOptionFilter';
import usePrevious from './helpers/usePrevious';

import styles from './Autocomplete.scss';

export { OptionGroup, Option } from '@ott/select';

// todo after InputNext typification
// Тип хелпер значений которые возвращает InputNext
type InputOptions = {
  isFocused: boolean;
  parse: Parser;
  formatter: Formatter;
  onChange: (value: string) => void;
};

export type AutocompleteProps<Option extends SelectOption = SelectOption> = {
  /** Нейм необходимый при использовании react hook form и FirstErrorShower'а */
  name?: string;
  /** Если вы используете rhf и FirstErrorShower'а вместе с fullscreenOnMobile,
   * т.к. модалка рендерится в портале, то инпут не будет в HTMLFormElement.elements,
   * это нужно чтобы сказать форме что этот автокомплит тоже принадлежит ей, без этого будет некорректно работать FES */
  form?: string;
  /** Кастомный локатор */
  specificLocator?: string;
  /** Задизейблен ли триггер */
  isDisabled?: boolean;
  /** Если true, то оверлей всегда будет снизу, нужно в случаях когда есть тултип,
   *  не нужно включать если включен shouldLimitHeightByScreen */
  isVerticalAutoPlacementDisabled?: boolean;
  /** Использовать ли виртуализацию для рендера списка опций,
   * для использования все опции должны быть строго одной высоты */
  isVirtualized?: boolean;
  /** Используется вместе с isVirtualized, нужно если у нас кастомная высота опций,
   *  иначе высота будет отталкиваться от size */
  virtualizationRowHeight?: number;
  /** Нужно ли красить бордер в красный, как правило для отображения ошибок */
  isInvalid?: boolean;
  /** Лейбл который в спокойном состоянии отображен как плейсхолдер, а в активном уходит в левый верхний угол */
  label?: string;
  /** Колбэк срабатывающий по закрытию дропдауна */
  onClose?: () => void;
  /** Колбэк срабатывающий по блюру инпута */
  onBlur?: () => void;
  /** Колбэк срабатывающий по фокусу инпута */
  onFocus?: () => void;
  /** Колбэк срабатывающий по клику на триггер */
  onTriggerClick?: () => void;
  /** Кастомный рендер опшена в оверлее */
  CustomOptionRenderer?: SelectCustomRenderer<CustomOptionProps<Option>>;
  /** Класс контейнера селекта */
  className?: string;
  /** С этим пропом автоматическое изменение расположения выключиться и и высота будет обрезаться по экрану */
  shouldLimitHeightByScreen?: boolean;
  /** Фокусирует первый опшен при открытии оверлея */
  shouldSetInitialHover?: boolean;
  /** Классы внутренних элементов */
  childrenClassNames?: {
    triggerChildrenClassNames?: {
      label?: string;
      input?: string;
      icon?: string;
      placeholder?: string;
    };
    trigger?: string;
    select?: Omit<SelectProps['childrenClassNames'], 'label' | 'placeholder'>;
  };
  /** Как оверлей показывается относительно триггера,
   * triggerWidth - оверлей будет по ширине всего триггера */
  alignType?: AlignType;
  /** Если false, то label в активном состоянии исчезнет*/
  withTitle?: boolean;
  /** Маска используемая в инпуте */
  mask?: string;
  /** Парсинг значения которое мы вводим, например если мы не хотим чтобы юзер вводил цифры, то кидаеем сюда регулярку */
  parse?: InputOptions['parse'];
  /** Значение перед тем как отобразиться в инпуте будет форматироваться по переданной ф-ии */
  formatter?: InputOptions['formatter'];
  /** Максимальная длина вводимого значения */
  maxLength?: number;
  /** Мод инпута (text, number, phone, email и тд) */
  inputMode?: React.HTMLAttributes<HTMLInputElement>['inputMode'];
  /** Браузерский автофилл, если нужно вырубить ставьте new-password */
  autoComplete?: string;
  /** Контент который отобразится в правом углу инпута */
  postfixNode?: ReactNode;
  /** Проп отвечающий за отображение автокомплита в модалке на весь экран в адаптиве */
  fullscreenOnMobile?: boolean;
  /** Тайтл для модалки в адаптиве с пропом fullScreenOnMobile = true */
  modalTitle?: string;
  /** Реф, передаваемый в input */
  forwardedRef?: ForwardedRef<HTMLInputElement>;
  /** Функция по которой будут фильтроваться варианты */
  optionFilter?: (
    options: (Option | OptionGroupProps)[],
    value: string
  ) => (Option | OptionGroupProps)[];
  /** Контейнер в котором будет рендерится оверлей */
  container?: null | HTMLElement;
} & (OptionsByChildren<Option> | OptionsByProp<Option>) &
  (ControlledByInput<Option> | ControlledByOptions<Option>);

// Таким образом будет просить либо опшены через проп либо через чилдрен
type OptionsByProp<Option extends SelectOption> = {
  /** Варианты, которые будут отображаться в дропдауне */
  options: (Option | OptionGroupProps)[];
  /** Можем прокидывать опшены так же и через jsx вариант, принимает только Option и OptionGroup */
  children?: ReactElement | ReactElement[];
};

type OptionsByChildren<Option extends SelectOption> = {
  options?: (Option | OptionGroupProps)[];
  children: ReactElement | ReactElement[];
};

type ControlledByOptions<Option extends SelectOption> = {
  /** Сюда прокидывается value выбранного опшена */
  value: Option['value'] | null;
  /** Сюда прокидывается значение инпута, если его нужно контроллировать*/
  inputValue?: string;
  /** Колбэк срабатывающий при выборе варианта из дропдауна */
  onSelect: (value: Option['value'] | null, option: Option | null) => void;
  /** Колбэк срабатывающий при выборе варианта из дропдауна или изменении значения инпута */
  onChange?: (value: string) => void;
};

type ControlledByInput<Option extends SelectOption> = {
  value?: Option['value'] | null;
  inputValue: string;
  onSelect?: (valueId: Option['value'] | null, option: Option | null) => void;
  onChange: (value: string) => void;
};

// пропы которые улетят в триггер, нужно обязательно прокидывать эти пропы в селект, а затем как аргументы в функцию рендера,
// потому что если брать из окружения в функции рендерере, то инпут постоянно будет ререндериться, фокус спадать
// и интернет эксплорер воскресать (а мы этого не хотим)
type TriggerProps<Option extends SelectOption> = Pick<
  AutocompleteProps<Option>,
  | 'value'
  | 'formatter'
  | 'mask'
  | 'parse'
  | 'isDisabled'
  | 'onChange'
  | 'isInvalid'
  | 'label'
  | 'childrenClassNames'
  | 'autoComplete'
  | 'inputMode'
  | 'postfixNode'
  | 'withTitle'
  | 'maxLength'
  | 'form'
> & { isInputControlled: boolean; setValue: (value: string) => void };

function Autocomplete<Option extends SelectOption>({
  children,
  forwardedRef,
  fullscreenOnMobile,
  inputValue,
  modalTitle,
  name,
  onChange = _noop,
  onSelect = _noop,
  optionFilter = defaultOptionFilter,
  options,
  specificLocator,
  value,
  form,
  container,

  CustomOptionRenderer,

  alignType = ALIGN_TYPES.LEFT,
  childrenClassNames,
  className,

  isDisabled = false,
  isInvalid = false,

  isVirtualized,
  virtualizationRowHeight,
  isVerticalAutoPlacementDisabled = false,
  label = '',
  shouldLimitHeightByScreen = false,
  shouldSetInitialHover = true,

  autoComplete,
  formatter,
  postfixNode,
  inputMode,
  mask,
  maxLength,
  parse,
  withTitle = !!label,

  onBlur = _noop,
  onClose = _noop,
  onFocus = _noop,
  onTriggerClick = _noop,
}: AutocompleteProps<Option>): ReactElement {
  const [isOverlayOpen, setIsOverlayOpen] = useState(false);
  const [isInputFocused, setIsInputFocused] = useState(false);
  const [innerInputValue, setInnerInputValue] = useState(inputValue || '');

  const inputRef = useRef<HTMLInputElement | null>(null);

  const prevIsOverlayOpen = usePrevious(isOverlayOpen);
  const prevIsInputFocused = usePrevious(isInputFocused);

  const { isMobile } = useLayoutContext();

  const parsedOptions = options?.length ? options : getParsedSelectOptions<Option>(children);

  const selectedOption = parsedOptions.find(
    (option) => !isOptionGroup(option) && option.value === value
  );

  const willOpenInFullscreen = fullscreenOnMobile && isMobile;
  const isInputControlled = typeof inputValue === 'string';

  const handleBlur = useCallback(() => {
    setIsInputFocused(false);
  }, []);

  const filteredOptions = useMemo(
    () => optionFilter(parsedOptions, innerInputValue) as (OptionGroupProps | Option)[],
    [optionFilter, parsedOptions, innerInputValue]
  );

  const onOptionSelect = useCallback(
    (optionValue: SelectOption['value'] | null, option: Option | null) => {
      if (optionValue === null || !option) {
        return;
      }

      const chosenOptionLabel = option.label;

      if (!isInputControlled) {
        setInnerInputValue(chosenOptionLabel);
      }

      onSelect(optionValue, option);
      onChange(chosenOptionLabel);
    },
    [onChange, onSelect, isInputControlled]
  );

  const renderTrigger = useCallback(
    ({
      isOverlayOpen: isOpen,
      openOverlay,
      closeOverlay,
      setRef,
      triggerProps: {
        autoComplete,
        childrenClassNames,
        formatter,
        postfixNode,
        inputMode,
        isDisabled,
        isInputControlled,
        isInvalid,
        label,
        mask,
        maxLength,
        onChange,
        parse,
        setValue,
        value,
        withTitle,
        form,
      },
    }: CustomTriggerProps<TriggerProps<Option>>) => {
      const handleChange = (newValue: string) => {
        if (!isOpen && value !== newValue) {
          openOverlay();
        }

        if (!isInputControlled) {
          setValue(newValue);
        }

        onChange!(newValue);
      };

      const onKeyDown: KeyboardEventHandler<HTMLInputElement> = (event) => {
        const key = event.key;

        if (key === KEYS.TAB) {
          closeOverlay();
        }
      };

      const handleFocus = () => {
        if (!isOverlayOpen) {
          openOverlay();
        }
        setIsInputFocused(true);
      };

      setIsOverlayOpen(isOpen);

      return (
        <div className={styles.trigger}>
          <Input
            maxLength={maxLength}
            onKeyDown={onKeyDown}
            postfix={postfixNode}
            inputMode={inputMode}
            autoComplete={autoComplete}
            formatter={formatter}
            parser={parse}
            mask={mask}
            ref={(node) => {
              inputRef.current = node;
              setRef.current = node;
            }}
            name={name}
            form={form}
            className={childrenClassNames?.trigger}
            childrenClassNames={childrenClassNames?.triggerChildrenClassNames || {}}
            onClick={isDisabled ? _noop : openOverlay}
            onFocus={isDisabled ? _noop : handleFocus}
            onChange={handleChange}
            isDisabled={isDisabled}
            hasError={isInvalid}
            label={withTitle ? label : undefined}
            placeholder={withTitle ? undefined : label}
            value={value ? String(value) : ''}
            specificLocators={[
              `${specificLocator || 'autocomplete'}-trigger${isDisabled ? '-disabled' : ''}`,
            ]}
          />
        </div>
      );
    },
    []
  );

  // проставляем форвард реф
  useEffect(() => {
    if (forwardedRef && inputRef.current) {
      (forwardedRef as MutableRefObject<HTMLInputElement>).current = inputRef.current;
    }
  }, [inputRef, forwardedRef, isOverlayOpen]);

  useEffect(() => {
    // строгая проверка нужна чтобы не вызывать при маунте
    if (prevIsOverlayOpen === true && !isOverlayOpen) {
      onClose();
    }
  }, [isOverlayOpen]);

  useEffect(() => {
    // строгая проверка нужна чтобы не вызывать при маунте
    if (prevIsInputFocused === false && isInputFocused && !willOpenInFullscreen) {
      onFocus();
    } else if (prevIsInputFocused === true && !isInputFocused) {
      onBlur();
    }
  }, [isInputFocused]);

  useEffect(() => {
    // проставляем фокус на инпут при открытии автокомплита в модалке
    if (willOpenInFullscreen && prevIsOverlayOpen === false && isOverlayOpen && inputRef.current) {
      onFocus();
      inputRef.current.focus();
    }
  }, [willOpenInFullscreen, inputRef, isOverlayOpen]);

  // если инпут не контролируемый, то мы проставляем в инпут лейбл только что изменившегося опшена
  useEffect(() => {
    if (
      selectedOption &&
      !isInputControlled &&
      typeof value !== 'undefined' &&
      parsedOptions.length > 0
    ) {
      setInnerInputValue(selectedOption.label);
    }
  }, [value]);

  useEffect(() => {
    if (!value) {
      setInnerInputValue('');
    }
  }, [value]);

  // если инпут контролируемый, то смотрим на изменение инпут вэлью сверху и меняем его изнутри
  useEffect(() => {
    if (isInputControlled) {
      setInnerInputValue(inputValue);
    }
  }, [inputValue]);

  const onClearByEscape = () => {
    onSelect(null, null);
    isInputControlled ? onChange('') : setInnerInputValue('');
  };

  return (
    <Select
      alignType={alignType}
      className={className}
      CustomOptionRenderer={CustomOptionRenderer}
      CustomTriggerRenderer={renderTrigger}
      customTriggerProps={{
        value: innerInputValue,
        setValue: setInnerInputValue,
        isInputControlled,
        formatter,
        mask,
        parse,
        isDisabled,
        isInvalid,
        onChange,
        label,
        childrenClassNames,
        autoComplete,
        inputMode,
        postfixNode,
        withTitle,
        maxLength,
        form,
      }}
      onBlur={handleBlur}
      onTriggerClick={onTriggerClick}
      onChange={onOptionSelect}
      options={filteredOptions}
      shouldLimitHeightByScreen={shouldLimitHeightByScreen}
      shouldSetInitialHover={shouldSetInitialHover}
      isVirtualized={isVirtualized}
      virtualizationRowHeight={virtualizationRowHeight}
      isVerticalAutoPlacementDisabled={isVerticalAutoPlacementDisabled}
      childrenClassNames={childrenClassNames?.select || {}}
      specificLocator={specificLocator}
      fullscreenOnMobile={fullscreenOnMobile}
      modalTitle={modalTitle}
      value={value || null}
      container={container}
    />
  );
}

const AutocompleteWithRef = memo(
  forwardRef((props: any, ref?: ForwardedRef<HTMLInputElement>) => {
    return <Autocomplete {...props} forwardedRef={ref} />;
  })
) as <Option extends SelectOption>(
  props: AutocompleteProps<Option> & { ref?: ForwardedRef<HTMLInputElement> }
) => ReactElement;
//@ts-ignore
AutocompleteWithRef.displayName = 'AutocompleteWithRef';

export default AutocompleteWithRef;
